import CustomProvider from 'components/CustomProvider';
import { useContext, useRef, ReactNode, createContext } from 'react';
import { Context } from '@alltrails/shared/types/context';

// used for trail card analytics
enum PageSource {
  Home = 'homepage',
  Explore = 'explore',
  Trail = 'trail',
  Seo = 'seo',
  Unknown = 'unknown'
}

type PageState = {
  context: Context;
  pageSource: PageSource;
};

type PageValue = {
  globalState: PageState;
  modalRef: React.RefObject<HTMLDivElement>;
};

const PageContext = createContext<PageValue>({
  globalState: { context: {} as Context, pageSource: PageSource.Unknown },
  modalRef: null
});

type PageProviderProps = {
  pageSource?: PageSource;
  context: Context;
  children: ReactNode;
};

const Page = ({ context, pageSource, children }: PageProviderProps): JSX.Element => {
  const modalRef = useRef<HTMLDivElement>(null);

  return (
    <PageContext.Provider value={{ globalState: { context, pageSource }, modalRef }}>
      <CustomProvider>
        <div id="modalPortal" ref={modalRef} />
        {children}
      </CustomProvider>
    </PageContext.Provider>
  );
};

function usePageContext() {
  const context = useContext(PageContext);

  if (context === undefined) {
    throw new Error('The page context is undefined. Are you using usePageContext within PageContext?');
  }

  return context.globalState.context;
}

export { Page, PageContext, PageSource, usePageContext };
